import { Box, Button, Stack, Typography } from "@mui/material";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { CouponListTable } from "Components/Admin/Coupons/CouponListTable";
import { CreateCouponDialog } from "Components/Admin/Coupons/CreateCouponDialog";
import { EditCouponDialog } from "Components/Admin/Coupons/EditCouponDialog";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { useDeleteCouponMutation } from "Api/Mutations/Coupons/useDeleteCouponMutation";
import {
  useCouponsQuery,
  type CouponsListParams,
} from "Api/Queries/Coupons/useCouponsQuery";
import {
  CouponDiscountType,
  CouponStatusFilter,
  type CouponDto,
} from "Infrastructure/Api/Api";
import { asEnumValue, asStartOfDayISO, asString } from "Utils/FilterUtils";
import AddIcon from "@mui/icons-material/Add";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { type ReactNode, useMemo, useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";
import { useServerTable } from "Hooks/useServerTable";

const RESOURCES = Resources.Admin.Coupons;

// Keep in sync with CouponsFilterRequest.AllowedSortColumns on BE.
const SORT_COLUMN_MAP: Record<string, string> = {
  usage: "Used",
};

export const CouponsPage = () => {
  const { t } = useResource();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [couponToEdit, setCouponToEdit] = useState<CouponDto | null>(null);
  const [couponToDelete, setCouponToDelete] = useState<CouponDto | null>(null);

  // "usage" and "status" are custom ids of computed columns in CouponListTable.
  const { queryParams, tableProps } = useServerTable<
    CouponDto,
    "usage" | "status"
  >({
    defaultSorting: [{ id: "couponID", desc: true }],
  });

  const apiParams = useMemo(
    (): CouponsListParams => ({
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: queryParams.sortBy
        ? (SORT_COLUMN_MAP[queryParams.sortBy] ?? queryParams.sortBy)
        : undefined,
      sortDirection: queryParams.sortDirection,
      code: asString(queryParams.filters.code),
      status: asEnumValue(CouponStatusFilter, queryParams.filters.status),
      discountType: asEnumValue(
        CouponDiscountType,
        queryParams.filters.discount,
      ),
      expirationDateFrom: asStartOfDayISO(queryParams.filters.expirationDate),
    }),
    [queryParams],
  );

  const { data, isLoading, isError } = useCouponsQuery(apiParams);

  const {
    isOpen: isDeleteDialogOpen,
    open: openDeleteDialog,
    handleResult: handleDeleteResult,
  } = useConfirmDialog();

  const { mutate: deleteCoupon } = useDeleteCouponMutation({
    onSuccess: () => {
      toast.success(t(RESOURCES.Messages.DeleteSuccess));
      setCouponToDelete(null);
    },
    onError: () => {
      toast.error(t(RESOURCES.Messages.DeleteError));
    },
  });

  if (!isLoading && (isError || !data)) {
    return (
      <Layout>
        <ErrorText>{t(RESOURCES.Messages.LoadError)}</ErrorText>
      </Layout>
    );
  }

  const handleDeleteRequest = (couponID: number) => {
    const coupon = data?.items.find((c) => c.couponID === couponID);
    if (coupon) {
      setCouponToDelete(coupon);
      openDeleteDialog();
    }
  };

  return (
    <Layout>
      <Stack gap={4}>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateDialogOpen(true)}
          >
            {t(RESOURCES.CreateButton)}
          </Button>
        </Box>
        <Box display="grid" alignItems="start">
          <CouponListTable
            data={data?.items ?? []}
            isLoading={isLoading}
            onDelete={handleDeleteRequest}
            onRowClick={setCouponToEdit}
            rowCount={data?.totalCount ?? 0}
            tableProps={tableProps}
          />
        </Box>
      </Stack>
      <CreateCouponDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />
      {couponToEdit && (
        <EditCouponDialog
          isOpen
          coupon={couponToEdit}
          onClose={() => setCouponToEdit(null)}
        />
      )}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        handleResult={handleDeleteResult(() => {
          if (couponToDelete) {
            deleteCoupon(couponToDelete.couponID);
          }
        })}
        modalTitle={t(RESOURCES.Messages.DeleteConfirmTitle)}
        modalContent={
          <Typography>
            {t(RESOURCES.Messages.DeleteConfirm, {
              code: couponToDelete?.code ?? "",
            })}
          </Typography>
        }
        isDestructive
      />
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout title={t(RESOURCES.PageTitle)}>
      <StyledPageWrapper maxWidth="unset !important">
        {children}
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default CouponsPage;
